import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useId, useRef, useState } from "react";
import { ImageCropperDialog } from "./ImageCropperDialog";
import { uploadImageToS3, checkExactDimensions, checkImageDimensions } from "../utils/functions/uploadToS3";
import toast from "react-hot-toast";
import { compressImageToJpeg } from "../utils/functions/compressImage";
import { getAdminFacingApiErrorMessage } from "../utils/functions/api-errors";
import "./inputFileCustom.css";

interface InputFileCustomProps {
  handleInputFile: (value: string) => void;
  handleInputFileClear: () => void;
  error?: string;
  buttonText?: string;
  disabled?: boolean;
  initialImage?: string;
  /** Width in px for the crop area. Defaults to 1290 */
  cropWidth?: number;
  /** Height in px for the crop area. Defaults to 550 */
  cropHeight?: number;
  /** S3 folder for upload. Defaults to "covers" */
  uploadFolder?: "covers" | "gallery" | "banners";
  /** Metadatos de la imagen subida. Banners los manda a la API, que valida peso y proporcion. */
  onUploaded?: (meta: { url: string; sizeKb: number; width: number; height: number }) => void;
  /** When true, skip minimum dimension validation in cropper */
  skipDimensionCheck?: boolean;
  /**
   * Modo "mínimo sin recorte": con `minWidth`/`minHeight` seteados no se abre el
   * cropper. Se valida que la imagen llegue a ese tamaño y se sube tal cual, y
   * `onUploaded` reporta las dimensiones reales en vez de las del recorte.
   * Lo usa el banner del carrusel, que la app dibuja con `cover` y sin
   * proporción fija.
   */
  minWidth?: number;
  minHeight?: number;
  maxSizeKb?: number;
  onBusyChange?: (busy: boolean) => void;
  onError?: (message?: string) => void;
  imageClassName?: string;
}

export const InputFileCustom = ({
  handleInputFile,
  handleInputFileClear,
  error,
  buttonText = "Seleccionar imagen",
  disabled = false,
  initialImage = "",
  cropWidth = 1290,
  cropHeight = 550,
  uploadFolder = "covers",
  skipDimensionCheck = false,
  minWidth,
  minHeight,
  onUploaded,
  maxSizeKb,
  onBusyChange,
  onError,
  imageClassName,
}: InputFileCustomProps) => {
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [cropperImage, setCropperImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusyState] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const [dragging, setDragging] = useState(false);
  const inputId = `file-input-${useId().replace(/:/g, "")}`;
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setProfileImage(initialImage || null);
  }, [initialImage]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const setBusy = (value: boolean) => {
    busyRef.current = value;
    if (!mountedRef.current) return;
    setBusyState(value);
    onBusyChange?.(value);
  };

  const setImageError = (message?: string) => {
    if (!mountedRef.current) return;
    setUploadError(message);
    onError?.(message);
  };

  const reportError = (error: unknown) => {
    const message = maxSizeKb
      ? getAdminFacingApiErrorMessage(error, "Error al subir la imagen")
      : "Error al subir la imagen";
    setImageError(message);
    toast.error(message);
  };

  const resetDrag = () => {
    dragDepth.current = 0;
    setDragging(false);
  };

  const uploadPreparedImage = async (
    blob: Blob,
    fileName: string,
    dimensions?: { width: number; height: number }
  ) => {
    if (maxSizeKb && (blob.size > maxSizeKb * 1024 || blob.type !== "image/jpeg")) {
      throw new Error(`La imagen debe ser JPEG y no superar ${maxSizeKb} KB.`);
    }
    const s3Url = await uploadImageToS3(blob, uploadFolder, fileName);
    if (!mountedRef.current) return;
    setProfileImage(s3Url);
    handleInputFile(s3Url);
    onUploaded?.({
      url: s3Url,
      sizeKb: Math.ceil(blob.size / 1024),
      width: dimensions?.width ?? cropWidth,
      height: dimensions?.height ?? cropHeight,
    });
    setImageError(undefined);
    toast.success("Imagen subida exitosamente");
  };

  const processFile = async (file: File) => {
    if (disabled || busyRef.current) return;

    setBusy(true);
    setImageError(undefined);

    try {
      if (!file.type.startsWith("image/")) throw new Error("Seleccioná un archivo de imagen válido.");

      const usesMinimumMode = minWidth !== undefined && minHeight !== undefined;

      if (usesMinimumMode) {
        const { valid, width: realWidth, height: realHeight } = await checkImageDimensions(
          file,
          minWidth,
          minHeight
        );
        if (!realWidth || !realHeight) {
          throw new Error("No se pudo leer la imagen. Seleccioná otra imagen válida.");
        }
        if (!valid) {
          throw new Error(
            `La imagen debe medir al menos ${minWidth}x${minHeight}px. ` +
              `La imagen cargada es de ${realWidth}x${realHeight}px.`
          );
        }

        setUploading(true);
        try {
          const blob = maxSizeKb ? await compressImageToJpeg(file, maxSizeKb) : file;
          await uploadPreparedImage(blob, maxSizeKb ? "banner.jpg" : file.name, {
            width: realWidth,
            height: realHeight,
          });
        } catch (err) {
          reportError(err);
        } finally {
          setUploading(false);
          setBusy(false);
        }
        return;
      }

      const { exact, width, height } = await checkExactDimensions(file, cropWidth, cropHeight);
      if (!width || !height) throw new Error("No se pudo leer la imagen. Seleccioná otra imagen válida.");

      if (exact) {
        setUploading(true);
        try {
          const blob = maxSizeKb ? await compressImageToJpeg(file, maxSizeKb) : file;
          await uploadPreparedImage(blob, maxSizeKb ? "banner.jpg" : file.name);
        } catch (err) {
          reportError(err);
        } finally {
          setUploading(false);
          setBusy(false);
        }
      } else {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error("No se pudo leer la imagen."));
          reader.readAsDataURL(file);
        });
        if (mountedRef.current) {
          setCropperImage(dataUrl);
          setShowCropper(true);
        }
      }
    } catch (err) {
      reportError(err);
      setBusy(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.currentTarget;
    if (!files || files.length === 0) return;
    const file = files[0];
    e.currentTarget.value = "";
    await processFile(file);
  };

  const handleCropConfirm = async (croppedBlob: Blob) => {
    setShowCropper(false);
    setCropperImage(null);
    setUploading(true);

    try {
      await uploadPreparedImage(croppedBlob, "cover.jpg");
    } catch (err) {
      reportError(err);
    } finally {
      setUploading(false);
      setBusy(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setCropperImage(null);
    setBusy(false);
  };

  const handleClear = () => {
    if (disabled || busyRef.current) return;
    setProfileImage(null);
    setImageError(undefined);
    handleInputFileClear();
  };

  const dropEnabled = !!maxSizeKb && !disabled && !busy;
  const dropzoneState = dragging
    ? "drag-over"
    : (uploadError || error)
      ? "error"
      : profileImage
        ? "with-image"
        : "default";

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!dropEnabled) return;
    dragDepth.current += 1;
    setDragging(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!dropEnabled) return;
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    resetDrag();
    if (!dropEnabled) return;
    const file = event.dataTransfer.files[0];
    if (file) void processFile(file);
  };

  const pickerBlocked = disabled || busy;

  /**
   * El input queda siempre fuera de vista y se dispara por el `for` de los
   * labels — es el mismo mecanismo que usa el selector de portada de Crear
   * Evento. La versión anterior lo estiraba transparente sobre toda la caja
   * para que el clic cayera en cualquier lado: eso tapaba el ícono y el
   * preview, se comía el foco y no abría el explorador de archivos.
   */
  const openPicker = () => {
    if (!pickerBlocked) fileInputRef.current?.click();
  };

  const activateWithKeyboard = (event: React.KeyboardEvent) => {
    if (pickerBlocked || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    openPicker();
  };

  const fileInput = (
    <input
      ref={fileInputRef}
      id={inputId}
      name="profileImage"
      className="input-file-sr-only"
      type="file"
      accept="image/*"
      aria-label={buttonText}
      onChange={handleFile}
      disabled={pickerBlocked}
    />
  );

  const body = (
    <>
      {fileInput}
      {uploading ? (
        <div className="flex flex-column align-items-center gap-2 p-4">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span className="text-sm text-500">Subiendo imagen...</span>
        </div>
      ) : profileImage ? (
        <div className="input-file-preview">
          <Image
            src={profileImage}
            alt="profileImage"
            preview
            width={imageClassName ? undefined : "360"}
            imageClassName={imageClassName}
            pt={imageClassName ? { preview: { className: "banner-image-lightbox" } } : undefined}
          />
        </div>
      ) : (
        <label
          htmlFor={pickerBlocked ? undefined : inputId}
          className="input-file-empty"
          role="button"
          tabIndex={pickerBlocked ? -1 : 0}
          aria-disabled={pickerBlocked}
          onKeyDown={activateWithKeyboard}
        >
          {/* En la zona de drop el icono es una marca chica: el circulo gigante
              del selector clasico dejaba la caja de 200px de alto vacia. */}
          <i
            className={maxSizeKb ? "pi pi-image" : "pi pi-image p-5"}
            style={
              maxSizeKb
                ? { fontSize: "2.5rem", color: "var(--surface-d)" }
                : {
                    fontSize: "4em",
                    borderRadius: "50%",
                    backgroundColor: "var(--surface-b)",
                    color: "var(--surface-d)",
                  }
            }
          ></i>
          {!!maxSizeKb && (
            <span className="text-sm text-500">Arrastrá una imagen acá o hacé clic para elegirla</span>
          )}
        </label>
      )}
      {error && <small className="pt-2 p-error">{error}</small>}
      {uploadError && <small role="alert" className="p-error">{uploadError}</small>}
      {!skipDimensionCheck && (
        <small className="text-500" style={{ marginTop: "var(--space-6)" }}>
          Resolución mínima: {minWidth ?? cropWidth}x{minHeight ?? cropHeight}px
        </small>
      )}
      <div
        className="input-file-dropzone-actions flex align-items-center gap-2"
        style={{ marginTop: "var(--space-16)" }}
      >
        <label
          htmlFor={pickerBlocked ? undefined : inputId}
          role="button"
          tabIndex={pickerBlocked ? -1 : 0}
          aria-disabled={pickerBlocked}
          onKeyDown={activateWithKeyboard}
          className={`input-file-select-button flex align-items-center cursor-pointer border-1 border-round ${
            error ? "border-red-900" : ""
          } text-primary px-4 py-2 ${pickerBlocked ? "disabled-label" : ""}`}
          style={{ opacity: pickerBlocked ? 0.6 : 1 }}
        >
          {buttonText} <i className="pi pi-upload ml-2" aria-hidden="true"></i>
        </label>
        {profileImage && (
          <Button
            icon="pi pi-trash"
            type="button"
            rounded
            text
            severity="danger"
            onClick={handleClear}
            disabled={pickerBlocked}
            aria-label="Quitar imagen"
            tooltip="Quitar imagen"
            tooltipOptions={{ position: "top" }}
          />
        )}
      </div>

      {cropperImage && (
        <ImageCropperDialog
          visible={showCropper}
          image={cropperImage}
          cropWidth={cropWidth}
          cropHeight={cropHeight}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          skipDimensionCheck={skipDimensionCheck}
          maxSizeKb={maxSizeKb}
        />
      )}
    </>
  );

  if (!maxSizeKb) return body;

  return (
    <div
      className="input-file-dropzone"
      data-dropzone-state={dropzoneState}
      data-dropzone-empty={!profileImage && !uploading}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      // Solo el fondo de la caja: los clics en el label, el preview o el botón
      // de borrar ya los maneja cada control, y reenviarlos abriría el
      // explorador dos veces.
      onClick={(event) => { if (event.target === event.currentTarget) openPicker(); }}
    >
      {body}
    </div>
  );
};
